/**
 * ModeSelector Component
 * Inline segmented control in the chat header (right-aligned).
 * Title hover shows description; active mode uses the agent's brand color.
 */

import { useState } from 'react';
import { Tooltip } from 'antd';
import api from '@/services/api';
import './ModeSelector.css';

interface ModeConfig {
  enabled: boolean;
  title: string;
  description: string;
  default_index: number;
  modes: Array<{ label: string; description?: string; instruction: string }>;
}

interface ModeSelectorProps {
  config: ModeConfig;
  conversationId: string;
  activeIndex: number;
  onModeChange: (index: number) => void;
}

export function ModeSelector({ config, conversationId, activeIndex, onModeChange }: ModeSelectorProps) {
  const [saving, setSaving] = useState(false);

  if (!config.enabled || !config.modes?.length) return null;

  const handleSelect = async (index: number) => {
    if (index === activeIndex || saving) return;

    setSaving(true);
    onModeChange(index);
    try {
      await api.put(`/conversations/${conversationId}/mode`, { mode_index: index });
    } catch (err) {
      console.error('Failed to save mode:', err);
      onModeChange(activeIndex);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mode-selector-inline">
      {config.title && (
        <Tooltip
          title={config.description || undefined}
          overlayClassName="message-action-tooltip"
          placement="bottom"
        >
          <span className="mode-selector-inline-title" dir="auto">{config.title}</span>
        </Tooltip>
      )}
      <div className="mode-selector-segments" role="tablist">
        {config.modes.map((mode, idx) => (
          <Tooltip
            key={idx}
            title={mode.description || undefined}
            overlayClassName="message-action-tooltip"
            placement="bottom"
          >
            <button
              type="button"
              role="tab"
              aria-selected={idx === activeIndex}
              className={`mode-selector-btn ${idx === activeIndex ? 'active' : ''}`}
              onClick={() => handleSelect(idx)}
              disabled={saving}
            >
              {mode.label}
            </button>
          </Tooltip>
        ))}
      </div>
    </div>
  );
}
